import Icon from "@components/Atoms/Icon";
import t from "@ext/localization/locale/translate";
import AddFilter from "@ext/markdown/elements/view/edit/components/Helpers/AddFilter";
import Menu from "@ext/markdown/elements/view/edit/components/Helpers/Orderby/Menu";
import ViewButton from "@ext/markdown/elements/view/edit/components/Helpers/ViewButton";
import type { ViewSettings } from "@ext/markdown/elements/view/models/ViewSettings";
import { getInputComponent } from "@ext/properties/components/Helpers/CustomInputRenderer";
import { isHasValue } from "@ext/properties/models";
import { Display } from "@ext/properties/models/display";
import { DropdownMenuRadioGroup, DropdownMenuRadioItem } from "@ui-kit/Dropdown";
import { useCallback } from "react";

interface ViewActionsProps {
	settings: ViewSettings;
	updateDisplay: (display: Display) => void;
	updateAttributes: (attributes: Partial<ViewSettings>) => void;
}

const ViewActions = ({ settings, updateDisplay, updateAttributes }: ViewActionsProps) => {
	const { defs, orderby, groupby, select, display } = settings;

	const getOrderbyValues = useCallback(
		(propertyName: string) => orderby.find((attr) => attr.id === propertyName)?.value,
		[orderby],
	);

	return (
		<>
			<AddFilter
				attributeName="defs"
				closeOnSelection={false}
				icon="filter"
				ignoreEmpty
				properties={defs}
				tooltipText={t("properties.view.filter")}
				updateAttributes={updateAttributes}
			/>
			<AddFilter
				allowSystemProperties={false}
				attributeName="orderby"
				availableValues
				closeOnSelection={false}
				customPropertyMenu={(property, updateData) => {
					if (isHasValue[property.type] && !getInputComponent(property.type))
						return (
							<Menu
								data={getOrderbyValues(property.id)?.filter((v) => v !== "none") || property.values}
								defaultData={property.values}
								name={property.id}
								updateData={updateData}
							/>
						);
				}}
				icon="arrow-down-a-z"
				mode="multiple"
				properties={orderby}
				tooltipText={t("properties.view.order-by")}
				updateAttributes={updateAttributes}
			/>
			<AddFilter
				allowSystemProperties={false}
				attributeName="groupby"
				availableValues={false}
				icon="list-tree"
				mode={display === Display.Kanban ? "single" : "multiple"}
				properties={groupby}
				tooltipText={t("properties.view.group-by")}
				updateAttributes={updateAttributes}
			/>
			<AddFilter
				allowSystemProperties={false}
				attributeName="select"
				availableValues={false}
				icon="list-checks"
				properties={select}
				tooltipText={t("properties.view.select")}
				updateAttributes={updateAttributes}
			/>
			<ViewButton icon="eye" tooltipText={t("properties.view.displays.name")}>
				<DropdownMenuRadioGroup indicatorIconPosition="end" onValueChange={updateDisplay} value={display}>
					<DropdownMenuRadioItem value={Display.List}>
						<Icon code="list" />
						{t("properties.view.displays.list")}
					</DropdownMenuRadioItem>
					<DropdownMenuRadioItem value={Display.Kanban}>
						<Icon code="square-kanban" />
						{t("properties.view.displays.kanban")}
					</DropdownMenuRadioItem>
					<DropdownMenuRadioItem value={Display.Table}>
						<Icon code="table" />
						{t("properties.view.displays.table")}
					</DropdownMenuRadioItem>
				</DropdownMenuRadioGroup>
			</ViewButton>
		</>
	);
};

export default ViewActions;
